import React, { useEffect, useState } from 'react';
import { useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { addLayer, allSteps, getMainConfig, selectLayers, updateStep } from '../../../../../../redux/stepsSlice';
import { getActiveStep, removeActiveLayer, setActiveStep } from '../../../../../../redux/toolsSlice';
import { getMaterial } from '../../../../../../redux/materialSlice';
import { getValue } from '../../../../utils/tools';
import { DateIcon } from '../../../../../assets/Icons';
import PreviewHeader from '../../../PreviewHeader';
import Renderer from '../Renderer';
import EmptyContent from '../content/EmptyContent';
import ButtonGroup from '../content/fields/ButtonGroup';
import LargeTextContent from '../content/fields/LargeTextContent';

// CanvasV2 — the page-builder canvas. Renders the real learner page (hero via
// PreviewHeader + a step stack) using the existing content renderers, so inline
// editing, add-element, and the data model are unchanged. Styling matches the
// approved mockup (learner-fidelity cards, progress preview, self-status control).
const STATUS_DOTS = [
  { label: 'Not started', c: '#B7C0C6' },
  { label: 'In progress', c: '#0FA3B8', on: true },
  { label: 'Stuck', c: '#C97A16' },
  { label: 'Completed', c: '#1E9E63' },
  { label: 'Skipped', c: '#B7C0C6' },
];

const CanvasV2 = ({ previewDevice = 'desktop', onSelectPath, onSelectStep }) => {
  const dispatch = useDispatch();
  const steps = useSelector((state) => allSteps(state));
  const activeStep = useSelector((state) => getActiveStep(state));
  const material = useSelector((state) => getMaterial(state));
  const mainConfig = useSelector((state) => getMainConfig(state));
  const layers = useSelector((state) => selectLayers(state, activeStep?.id));

  const mainLayer = layers.find((l) => l.id === 'main') || layers.find((l) => l.type === 'large_text');
  const contentLayers = layers.filter((l) => l.id !== mainLayer?.id);
  const canRender = contentLayers.length > 0;

  const config = Array.isArray(mainConfig) && mainConfig.length > 0 ? mainConfig : material?.config;
  const defaultColors = { background: '#F6F8F9', text: '#363841', config };
  const caHeaderColor = getValue(defaultColors, 'ca', 'caHeaderColor');
  const caHeaderFontSize = getValue(defaultColors, 'ca', 'caHeaderFontSize');
  const caTextColor = getValue(defaultColors, 'ca', 'caTextColor');
  const contentAreaStyle = {
    '--path-content-header-bg': caHeaderColor || '#F3F3F3',
    '--path-content-header-font-size': caHeaderFontSize || '18px',
    '--path-content-header-text': caTextColor || '#363841',
  };

  const [dueDate, setDueDate] = useState('');
  const [isBodyOpen, setIsBodyOpen] = useState(true);

  useEffect(() => {
    setDueDate(activeStep?.dueDate || '');
    if (activeStep?.id && layers && layers.length === 0) {
      dispatch(addLayer({
        stepId: activeStep.id,
        layer: { id: 'main', type: 'large_text', fontSize: 34, color: '#000000', fontFamily: 'Open Sans', content: 'This is large title' },
      }));
    }
  }, [activeStep?.id]);

  useEffect(() => { setIsBodyOpen(true); }, [activeStep?.id]);

  const [, drop] = useDrop(() => ({ accept: 'card', drop: () => ({ name: 'CanvasV2' }) }));

  const activeIndex = steps.findIndex((s) => s.id === activeStep?.id);

  const openStep = (step) => {
    if (onSelectStep) onSelectStep();
    if (step.id === activeStep?.id) {
      setIsBodyOpen((prev) => !prev);
      if (isBodyOpen) dispatch(removeActiveLayer());
      return;
    }
    dispatch(setActiveStep(step));
    dispatch(removeActiveLayer());
    setIsBodyOpen(true);
  };

  return (
    <div className="un-pe-canvas">
      <div className="un-pe-device" style={contentAreaStyle}>
        <div
          className="un-pe-herohit"
          role="presentation"
          onClick={() => { if (onSelectPath) onSelectPath(); dispatch(removeActiveLayer()); }}
        >
          <PreviewHeader editable />
        </div>

        <div className="un-pe-progress">
          <div className="un-pe-progress__r1">
            <span className="un-pe-eyebrow">Your progress</span>
            <span className="un-pe-eyebrow" style={{ color: '#B7C0C6' }}>Preview</span>
          </div>
          <div className="un-pe-progress__pt">0 of {steps.length} steps completed</div>
          <div className="un-pe-progress__bar"><i /></div>
          <div className="un-pe-progress__leg">
            <span><i style={{ background: '#B7C0C6' }} />{steps.length} not started</span>
            <span><i style={{ background: '#0FA3B8' }} />0 in progress</span>
            <span><i style={{ background: '#1E9E63' }} />0 completed</span>
          </div>
        </div>

        <div className="un-pe-stack">
          {steps.map((step, index) => {
            const isActive = step.id === activeStep?.id;
            const isOpen = isActive && isBodyOpen;
            const hasRule = step.display_rule && Array.isArray(step.display_rule.conditions)
              && step.display_rule.conditions.some((c) => c && c.attribute);
            return (
              <article key={step.id} className={`un-pe-step ${isOpen ? 'is-open' : 'is-collapsed'}`}>
                <div className="un-pe-step__hd" onClick={() => openStep(step)}>
                  <span className="un-pe-step__n">{index + 1}</span>
                  <div style={{ minWidth: 0 }}>
                    {step.name && step.name !== step.title && (
                      <div className="un-pe-eyebrow un-pe-step__eb">{step.name}</div>
                    )}
                    <div className="un-pe-step__tt">{step.title || step.name || `Step ${index + 1}`}</div>
                  </div>
                  <div className="un-pe-step__right">
                    {hasRule && <span className="un-pe-rulechip">Rule</span>}
                    <span className={`un-pe-pill ${isActive ? 'un-pe-pill--prog' : 'un-pe-pill--ns'}`}>
                      <i style={{ background: isActive ? '#0FA3B8' : '#B7C0C6' }} />
                      {isActive ? 'Editing' : index < activeIndex ? 'Done' : 'Pending'}
                    </span>
                  </div>
                </div>

                {isOpen && (
                  <div className="un-pe-step__body" ref={drop}>
                    <div className="element-wrapper element-wrapper--hero">
                      {mainLayer ? <LargeTextContent layer={mainLayer} /> : (
                        <h2 className="path-step-content__fallback-title">{activeStep?.title || 'Current step'}</h2>
                      )}
                    </div>

                    {dueDate && (
                      <div className="un-pe-duechip">
                        <DateIcon />
                        <span>Due {moment(dueDate).format('MMM D, YYYY')}</span>
                        <button
                          type="button"
                          className="un-pe-duechip__x"
                          aria-label="Clear due date"
                          onClick={() => dispatch(updateStep({ ...activeStep, dueDate: '' }))}
                        >
                          &times;
                        </button>
                      </div>
                    )}

                    {canRender ? <Renderer layers={contentLayers} /> : <EmptyContent />}

                    <div className="element-wrapper" style={{ marginTop: 8 }}>
                      <ButtonGroup />
                    </div>

                    <div className="un-pe-selfstat">
                      <p className="un-pe-eyebrow" style={{ marginBottom: 8 }}>
                        How&apos;s this step going? <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: '#B7C0C6' }}>· learner control</span>
                      </p>
                      <div className="un-pe-seg">
                        {STATUS_DOTS.map((s) => (
                          <span key={s.label} className={`un-pe-seg__s${s.on ? ' on' : ''}`}>
                            <i style={{ background: s.c }} /> {s.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CanvasV2;
